// Portfolio — dedicated marketing page.
// Renders the full pitch: passport intelligent, pre-rempli IA, modules,
// audiences, diffusion, promesse.
//
// All copy is i18n-ready via the `portfolioP.*` keys (EN/FR/ES/PT).
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Globe2, BookOpen, FileText, Wine, GraduationCap, UtensilsCrossed,
  Film, BarChart3, User, Store, Plane, Award, Users, QrCode, ArrowRight, Brain,
  Database, Layers, Target, Workflow, Wand2, Share2, ChevronLeft, ChevronRight,
} from "lucide-react";
import Layout from "@/components/Layout";
import { useT } from "@/lib/i18n";

const MODULES = [
  { key: "origin",   icon: Globe2,            color: "text-[color:var(--gv-pink)]" },
  { key: "story",    icon: BookOpen,          color: "text-amber-600" },
  { key: "tech",     icon: FileText,          color: "text-sky-600" },
  { key: "tasting",  icon: Wine,              color: "text-[color:var(--gv-pink)]" },
  { key: "training", icon: GraduationCap,     color: "text-emerald-600" },
  { key: "pairing",  icon: UtensilsCrossed,   color: "text-orange-600" },
  { key: "media",    icon: Film,              color: "text-violet-600" },
  { key: "trade",    icon: BarChart3,         color: "text-rose-600" },
];

const AUDIENCES = [
  { key: "consumer",   icon: User },
  { key: "retailer",   icon: Store },
  { key: "importer",   icon: Plane },
  { key: "sommelier",  icon: Award },
  { key: "educator",   icon: Users },
];

const CHANNELS = ["qr_bottle", "qr_fair", "wine_list", "catalog", "export", "training", "private_link"];

const POSITIONING = ["entry", "premium", "super_premium", "icon"];

const PortfolioPage = () => {
  const t = useT();
  return (
    <Layout>
      {/* HERO */}
      <section className="relative bg-[color:var(--gv-ink)] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06] bg-[radial-gradient(circle_at_30%_20%,var(--gv-pink),transparent_60%)]" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-20 md:py-28">
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[color:var(--gv-pink)] text-white rounded-full text-[10px] uppercase tracking-[0.16em] font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
              {t("portfolioP.status.beta")}
            </span>
            <span className="text-[11px] text-white/55 italic">{t("portfolioP.status.note")}</span>
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 border border-white/15 rounded-full text-[10.5px] uppercase tracking-[0.18em] mb-6">
            <Sparkles size={11} className="text-[color:var(--gv-pink)]" /> {t("portfolioP.hero.eyebrow")}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6 max-w-3xl" data-testid="portfolio-hero-title">
            {t("portfolioP.hero.title")}
          </h1>
          <p className="text-lg text-white/75 max-w-2xl leading-relaxed mb-8">
            {t("portfolioP.hero.subtitle")}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/contact?subject=portfolio-beta"
                  className="btn-primary" data-testid="portfolio-cta-beta">
              {t("portfolioP.hero.cta_beta")} <ArrowRight size={14} />
            </Link>
            <Link to="/contact" className="btn-ghost !text-white !border-white/25 hover:!bg-white/10"
                  data-testid="portfolio-cta-contact">
              {t("portfolioP.hero.cta_contact")}
            </Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — 3 STEPS */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <div className="eyebrow mb-3">{t("portfolioP.how.eyebrow")}</div>
            <h2 className="font-display text-3xl md:text-5xl text-[color:var(--gv-ink)] max-w-2xl mx-auto">
              {t("portfolioP.how.title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
            {/* Subtle connecting line on desktop */}
            <div className="hidden md:block absolute top-[42px] left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-[color:var(--gv-pink)]/30 to-transparent" />
            {[
              { n: "01", icon: QrCode, key: "scan" },
              { n: "02", icon: Wand2,  key: "ai" },
              { n: "03", icon: Share2, key: "share" },
            ].map((s) => (
              <div key={s.n} className="relative bg-white border border-[color:var(--gv-line)] rounded-[5px] p-6 md:p-8 text-center hover:border-[color:var(--gv-pink)] transition-colors"
                   data-testid={`portfolio-step-${s.key}`}>
                <div className="relative inline-flex items-center justify-center w-20 h-20 rounded-full bg-[color:var(--gv-mist)] mx-auto mb-5">
                  <s.icon size={26} className="text-[color:var(--gv-pink)]" />
                  <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-7 h-7 rounded-full bg-[color:var(--gv-pink)] text-white font-display text-[12px]">
                    {s.n}
                  </span>
                </div>
                <h3 className="font-display text-xl text-[color:var(--gv-ink)] mb-2">{t(`portfolioP.how.${s.key}.title`)}</h3>
                <p className="text-sm text-[color:var(--gv-ink)]/65 leading-relaxed">{t(`portfolioP.how.${s.key}.desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ONE SOURCE OF TRUTH */}
      <section className="py-16 md:py-24 bg-[color:var(--gv-mist)]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <div className="eyebrow mb-3">{t("portfolioP.truth.eyebrow")}</div>
            <h2 className="font-display text-3xl md:text-5xl text-[color:var(--gv-ink)] max-w-3xl mx-auto">
              {t("portfolioP.truth.title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-6">
              <div className="text-xs uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-3">{t("portfolioP.truth.before")}</div>
              <ul className="space-y-2.5 text-sm text-[color:var(--gv-ink)]/80">
                {["files_pdf", "sales_decks", "translations", "training", "pitch"].map((k) => (
                  <li key={k} className="flex items-start gap-2">
                    <span className="text-[color:var(--gv-ink)]/40 mt-0.5">×</span>
                    <span>{t(`portfolioP.truth.before.${k}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[color:var(--gv-ink)] text-white rounded-[5px] p-6">
              <div className="text-xs uppercase tracking-widest text-[color:var(--gv-pink)] mb-3">{t("portfolioP.truth.after")}</div>
              <p className="text-[15px] leading-relaxed mb-4">{t("portfolioP.truth.after_desc")}</p>
              <p className="text-[13.5px] text-white/65 leading-relaxed">{t("portfolioP.truth.after_note")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* AI PRE-FILL */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <div className="eyebrow mb-3">{t("portfolioP.ai.eyebrow")}</div>
            <h2 className="font-display text-3xl md:text-5xl text-[color:var(--gv-ink)] max-w-3xl mx-auto mb-4">
              {t("portfolioP.ai.title")}
            </h2>
            <p className="text-[color:var(--gv-ink)]/65 max-w-2xl mx-auto leading-relaxed">{t("portfolioP.ai.subtitle")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[color:var(--gv-mist)] flex items-center justify-center">
                  <Database size={18} className="text-[color:var(--gv-pink)]" />
                </div>
                <h3 className="font-display text-xl text-[color:var(--gv-ink)]">{t("portfolioP.ai.geovinum_title")}</h3>
              </div>
              <ul className="grid grid-cols-2 gap-2 text-sm text-[color:var(--gv-ink)]/75">
                {["region","appellation","grapes","climate","soils","altitude","plots","producers","history","geo"].map((k) => (
                  <li key={k} className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-[color:var(--gv-pink)]" />
                    {t(`portfolioP.ai.geovinum.${k}`)}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[color:var(--gv-mist)] flex items-center justify-center">
                  <Brain size={18} className="text-[color:var(--gv-pink)]" />
                </div>
                <h3 className="font-display text-xl text-[color:var(--gv-ink)]">{t("portfolioP.ai.ai_title")}</h3>
              </div>
              <ul className="space-y-1.5 text-sm text-[color:var(--gv-ink)]/75">
                {["summary","storytelling","sales","retailer","export","pairing","profiles","training","i18n"].map((k) => (
                  <li key={k} className="flex items-start gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-[color:var(--gv-pink)] mt-2" />
                    {t(`portfolioP.ai.ai.${k}`)}
                  </li>
                ))}
              </ul>
              <p className="text-[12.5px] text-[color:var(--gv-ink)]/55 italic mt-5 leading-relaxed">
                {t("portfolioP.ai.editable")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MODULES */}
      <section className="py-16 md:py-24 bg-[color:var(--gv-mist)]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <div className="eyebrow mb-3">{t("portfolioP.modules.eyebrow")}</div>
            <h2 className="font-display text-3xl md:text-5xl text-[color:var(--gv-ink)] max-w-3xl mx-auto">
              {t("portfolioP.modules.title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {MODULES.map((m) => (
              <div key={m.key} className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5 hover:border-[color:var(--gv-pink)] transition-colors"
                   data-testid={`portfolio-module-${m.key}`}>
                <m.icon size={22} className={`${m.color} mb-3`} />
                <h3 className="font-display text-lg text-[color:var(--gv-ink)] mb-2">{t(`portfolioP.module.${m.key}.title`)}</h3>
                <p className="text-[12.5px] text-[color:var(--gv-ink)]/65 leading-relaxed">{t(`portfolioP.module.${m.key}.desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTERACTIVE PORTFOLIO MOCK */}
      <PortfolioMockCarousel t={t} />

      {/* MARKET POSITIONING */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <div className="eyebrow mb-3">{t("portfolioP.position.eyebrow")}</div>
            <h2 className="font-display text-3xl md:text-5xl text-[color:var(--gv-ink)] max-w-3xl mx-auto mb-4">
              {t("portfolioP.position.title")}
            </h2>
            <p className="text-[color:var(--gv-ink)]/65 max-w-2xl mx-auto leading-relaxed">{t("portfolioP.position.subtitle")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
            <PositionCard icon={Target} keyName="position" t={t} />
            <PositionCard icon={Workflow} keyName="competitive" t={t} />
            <PositionCard icon={BarChart3} keyName="value" t={t} />
            <PositionCard icon={Sparkles} keyName="differentiation" t={t} />
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2 mt-8">
            {POSITIONING.map((p) => (
              <span key={p} className="px-3 py-1 bg-[color:var(--gv-mist)] border border-[color:var(--gv-line)] rounded-full text-[12px] text-[color:var(--gv-ink)]/75">
                {t(`portfolioP.position.tier.${p}`)}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIENCES */}
      <section className="py-16 md:py-24 bg-[color:var(--gv-ink)] text-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 border border-white/15 rounded-full text-[10.5px] uppercase tracking-[0.18em] mb-3">
              <Layers size={11} className="text-[color:var(--gv-pink)]" /> {t("portfolioP.audience.eyebrow")}
            </div>
            <h2 className="font-display text-3xl md:text-5xl max-w-3xl mx-auto">{t("portfolioP.audience.title")}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {AUDIENCES.map((a) => (
              <div key={a.key} className="bg-white/5 border border-white/10 rounded-[5px] p-5 hover:bg-white/[0.08] transition-colors">
                <a.icon size={20} className="text-[color:var(--gv-pink)] mb-3" />
                <h3 className="font-display text-lg mb-2">{t(`portfolioP.audience.${a.key}.title`)}</h3>
                <ul className="space-y-1 text-[12.5px] text-white/65">
                  {t(`portfolioP.audience.${a.key}.list`).split("·").map((s) => (
                    <li key={s.trim()} className="leading-relaxed">— {s.trim()}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DIFFUSION CHANNELS */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <div className="eyebrow mb-3">{t("portfolioP.diffusion.eyebrow")}</div>
            <h2 className="font-display text-3xl md:text-5xl text-[color:var(--gv-ink)] max-w-3xl mx-auto mb-4">
              {t("portfolioP.diffusion.title")}
            </h2>
            <p className="text-[color:var(--gv-ink)]/65 max-w-2xl mx-auto leading-relaxed">{t("portfolioP.diffusion.subtitle")}</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {CHANNELS.map((c) => (
              <div key={c} className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-4 text-center hover:border-[color:var(--gv-pink)] transition-colors">
                <QrCode size={18} className="text-[color:var(--gv-pink)] mx-auto mb-2" />
                <div className="text-[13.5px] text-[color:var(--gv-ink)]">{t(`portfolioP.diffusion.${c}`)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROMISE */}
      <section className="py-20 md:py-28 bg-[color:var(--gv-mist)]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="eyebrow mb-3">{t("portfolioP.promise.eyebrow")}</div>
          <h2 className="font-display text-3xl md:text-5xl text-[color:var(--gv-ink)] mb-6 leading-[1.15]">
            {t("portfolioP.promise.title")}
          </h2>
          <p className="text-[15.5px] md:text-lg text-[color:var(--gv-ink)]/75 leading-relaxed mb-8">
            {t("portfolioP.promise.body")}
          </p>
          <div className="font-display text-xl md:text-2xl text-[color:var(--gv-pink)] mb-10">
            {t("portfolioP.promise.tagline")} 🍾🌍🤖📈
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/contact?subject=portfolio-beta" className="btn-primary" data-testid="portfolio-bottom-cta">
              {t("portfolioP.promise.cta")} <ArrowRight size={14} />
            </Link>
            <Link to="/contact" className="btn-ghost">{t("portfolioP.promise.cta_contact")}</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

const PositionCard = ({ icon: Icon, keyName, t }) => (
  <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-6 hover:border-[color:var(--gv-pink)] transition-colors">
    <Icon size={20} className="text-[color:var(--gv-pink)] mb-3" />
    <h3 className="font-display text-lg text-[color:var(--gv-ink)] mb-2">{t(`portfolioP.position.${keyName}.title`)}</h3>
    <p className="text-[13px] text-[color:var(--gv-ink)]/65 leading-relaxed">{t(`portfolioP.position.${keyName}.desc`)}</p>
  </div>
);

// =========================================================================
// Interactive Portfolio mock — a no-backend visual carousel showing what the
// SAME product looks like for 4 different audiences. Auto-advances every 5 s,
// with manual prev/next + audience pills.
// =========================================================================
const MOCK_VIEWS = [
  {
    key: "consumer",
    accent: "from-rose-500/12 to-rose-500/0",
    sections: ["story", "terroir", "tasting", "pairing"],
    storyKey: "consumer.story",
  },
  {
    key: "retailer",
    accent: "from-amber-500/12 to-amber-500/0",
    sections: ["sales", "sensory", "similar", "price"],
    storyKey: "retailer.story",
  },
  {
    key: "sommelier",
    accent: "from-sky-500/12 to-sky-500/0",
    sections: ["tasting_detail", "pairing", "service"],
    storyKey: "sommelier.story",
  },
  {
    key: "educator",
    accent: "from-emerald-500/12 to-emerald-500/0",
    sections: ["wset", "maps", "quiz", "resources"],
    storyKey: "educator.story",
  },
];

const PortfolioMockCarousel = ({ t }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % MOCK_VIEWS.length), 5500);
    return () => clearInterval(id);
  }, []);
  const view = MOCK_VIEWS[idx];
  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <div className="eyebrow mb-3">{t("portfolioP.demo.eyebrow")}</div>
          <h2 className="font-display text-3xl md:text-5xl text-[color:var(--gv-ink)] max-w-3xl mx-auto mb-3">
            {t("portfolioP.demo.title")}
          </h2>
          <p className="text-[color:var(--gv-ink)]/65 max-w-2xl mx-auto">{t("portfolioP.demo.subtitle")}</p>
        </div>

        {/* Audience pills */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
          {MOCK_VIEWS.map((v, i) => (
            <button
              key={v.key}
              onClick={() => setIdx(i)}
              className={`px-4 py-1.5 rounded-full text-[12.5px] transition-all ${
                i === idx
                  ? "bg-[color:var(--gv-ink)] text-white"
                  : "bg-[color:var(--gv-mist)] text-[color:var(--gv-ink)]/65 hover:text-[color:var(--gv-ink)]"
              }`}
              data-testid={`mock-audience-${v.key}`}
            >
              {t(`portfolioP.audience.${v.key}.title`)}
            </button>
          ))}
        </div>

        {/* Mock device frame */}
        <div className="relative max-w-4xl mx-auto">
          <button
            onClick={() => setIdx((i) => (i - 1 + MOCK_VIEWS.length) % MOCK_VIEWS.length)}
            className="hidden md:flex absolute left-[-52px] top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-[color:var(--gv-line)] items-center justify-center hover:border-[color:var(--gv-pink)] transition-colors"
            aria-label="Previous" data-testid="mock-prev"
          >
            <ChevronLeft size={18} className="text-[color:var(--gv-ink)]" />
          </button>
          <button
            onClick={() => setIdx((i) => (i + 1) % MOCK_VIEWS.length)}
            className="hidden md:flex absolute right-[-52px] top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-[color:var(--gv-line)] items-center justify-center hover:border-[color:var(--gv-pink)] transition-colors"
            aria-label="Next" data-testid="mock-next"
          >
            <ChevronRight size={18} className="text-[color:var(--gv-ink)]" />
          </button>

          <div className="relative rounded-[10px] border border-[color:var(--gv-line)] overflow-hidden shadow-lg shadow-[color:var(--gv-ink)]/5">
            {/* Browser chrome */}
            <div className="flex items-center gap-1.5 px-4 py-2.5 bg-[color:var(--gv-mist)] border-b border-[color:var(--gv-line)]">
              <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]"></span>
              <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]"></span>
              <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]"></span>
              <div className="flex-1 text-center text-[11px] text-[color:var(--gv-ink)]/55 font-mono">
                portfolio.geovinum.io/château-margaux-2018
              </div>
            </div>

            {/* Mock content */}
            <div className={`relative bg-gradient-to-br ${view.accent} bg-white p-6 md:p-10 min-h-[420px] transition-all duration-500`} key={view.key}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
                {/* Left: product card */}
                <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-4">
                  <div className="aspect-[3/4] bg-gradient-to-b from-[color:var(--gv-ink)]/10 to-[color:var(--gv-pink)]/15 rounded mb-3 flex items-center justify-center">
                    <Wine size={48} className="text-[color:var(--gv-ink)]/30" />
                  </div>
                  <div className="font-display text-base text-[color:var(--gv-ink)]">Château Margaux</div>
                  <div className="text-[11px] text-[color:var(--gv-ink)]/55">2018 · Margaux · 1er Grand Cru Classé</div>
                  <div className="flex gap-1 mt-3">
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[color:var(--gv-pink)]/10 text-[color:var(--gv-pink)] rounded">Icon</span>
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[color:var(--gv-mist)] text-[color:var(--gv-ink)]/65 rounded">AOC</span>
                  </div>
                </div>

                {/* Right: audience-specific blocks */}
                <div className="md:col-span-2 space-y-3">
                  <div className="text-[10.5px] uppercase tracking-[0.18em] text-[color:var(--gv-pink)] font-medium">
                    {t(`portfolioP.demo.lens`)} · {t(`portfolioP.audience.${view.key}.title`)}
                  </div>
                  <p className="text-sm text-[color:var(--gv-ink)] leading-relaxed italic border-l-2 border-[color:var(--gv-pink)] pl-3">
                    {t(`portfolioP.demo.${view.storyKey}`)}
                  </p>
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    {view.sections.map((s) => (
                      <div key={s} className="bg-white border border-[color:var(--gv-line)] rounded px-3 py-2">
                        <div className="text-[9.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/45 mb-0.5">
                          {t(`portfolioP.demo.section.${s}`)}
                        </div>
                        <div className="text-[12px] text-[color:var(--gv-ink)]/80 leading-tight">
                          {t(`portfolioP.demo.section.${s}.value`)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Dots */}
          <div className="flex items-center justify-center gap-1.5 mt-5">
            {MOCK_VIEWS.map((v, i) => (
              <button
                key={v.key}
                onClick={() => setIdx(i)}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-[color:var(--gv-pink)]" : "w-1.5 bg-[color:var(--gv-ink)]/20"}`}
                aria-label={v.key}
              />
            ))}
          </div>
        </div>

        <p className="text-center text-[12px] text-[color:var(--gv-ink)]/55 italic mt-6 max-w-xl mx-auto">
          {t("portfolioP.demo.note")}
        </p>
      </div>
    </section>
  );
};

export default PortfolioPage;
